import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, ArrowLeft, Filter, Search } from "lucide-react";
import { Order } from "../types";
import apiService from "../services/api";

type SortBy = "date_desc" | "date_asc" | "amount_desc" | "amount_asc";

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    loadOrders();
  }, [filterStatus]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getMarketerOrders();
      setOrders(data.results || data);
    } catch (error) {
      console.error("Failed to load orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      submitted_for_buying: "bg-blue-100 text-blue-800",
      accepted: "bg-orange-100 text-orange-800",
      buying: "bg-purple-100 text-purple-800",
      waiting_delivery: "bg-yellow-100 text-yellow-800",
      submitted_for_delivery: "bg-indigo-100 text-indigo-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getFilteredAndSortedOrders = () => {
    let result = [...orders];

    // Filter by status
    if (filterStatus !== "all") {
      result = result.filter(order => order.currentStatus?.status === filterStatus);
    }

    // Filter by search query (order ID, market name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.orderId.toLowerCase().includes(query) ||
        order.market.name.toLowerCase().includes(query)
      );
    }

    // Sort orders
    result.sort((a, b) => {
      switch (sortBy) {
        case "date_desc":
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case "date_asc":
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case "amount_desc":
          return b.cost - a.cost;
        case "amount_asc":
          return a.cost - b.cost;
        default:
          return 0;
      }
    });

    return result;
  };

  const filteredOrders = getFilteredAndSortedOrders();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">All Orders</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Search, Sort, and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Sort Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID or market name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white min-w-[200px]"
              >
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="amount_desc">Highest Amount</option>
                <option value="amount_asc">Lowest Amount</option>
              </select>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  showFilters
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredOrders.length} of {orders.length} orders
            {searchQuery && ` for "${searchQuery}"`}
          </div>
        </div>

        {/* Status Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="font-medium text-gray-900">Filter by Status</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "all"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus("submitted_for_buying")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "submitted_for_buying"
                    ? "bg-blue-600 text-white"
                    : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                }`}
              >
                New Orders
              </button>
              <button
                onClick={() => setFilterStatus("accepted")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "accepted"
                    ? "bg-orange-600 text-white"
                    : "bg-orange-100 text-orange-800 hover:bg-orange-200"
                }`}
              >
                Accepted
              </button>
              <button
                onClick={() => setFilterStatus("buying")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "buying"
                    ? "bg-purple-600 text-white"
                    : "bg-purple-100 text-purple-800 hover:bg-purple-200"
                }`}
              >
                Buying
              </button>
              <button
                onClick={() => setFilterStatus("waiting_delivery")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "waiting_delivery"
                    ? "bg-yellow-600 text-white"
                    : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                }`}
              >
                Waiting Delivery
              </button>
              <button
                onClick={() => setFilterStatus("delivered")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "delivered"
                    ? "bg-green-600 text-white"
                    : "bg-green-100 text-green-800 hover:bg-green-200"
                }`}
              >
                Delivered
              </button>
              <button
                onClick={() => setFilterStatus("cancelled")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === "cancelled"
                    ? "bg-red-600 text-white"
                    : "bg-red-100 text-red-800 hover:bg-red-200"
                }`}
              >
                Cancelled
              </button>
            </div>
          </div>
        )}

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchQuery ? `No orders found matching "${searchQuery}"` : "No orders found"}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 text-primary hover:text-orange-600 text-sm font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                onClick={() => navigate(`/orders/${order.id}`)}
                className="bg-white border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer p-4"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-lg">
                        #{order.orderId}
                      </p>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(
                          order.currentStatus?.status || ""
                        )}`}
                      >
                        {formatStatus(order.currentStatus?.status || "")}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {order.market.name}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span>{order.marketList.marketListItem?.length || 0} items</span>
                      <span className="text-gray-400">•</span>
                      <span>{new Date(order.date).toLocaleDateString()}</span>
                      {order.currentStatus?.date && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-xs">
                            Updated {new Date(order.currentStatus.date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-4 mt-3">
                      <div>
                        <p className="text-2xl font-bold text-primary">
                          {order.cost.toFixed(0)} FCFA
                        </p>
                        <p className="text-xs text-gray-500">Shopping budget</p>
                      </div>
                      <button className="px-6 py-2 bg-primary hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                        View Details →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
